// #271 (Paul 03.11.2026): Aufgaben nach Dringlichkeit statt nach Betrieb,
// Herkunft lesbar statt „email:1003“. Reine Funktionen, damit die Seite
// und die Tests dieselbe Einteilung nutzen.
import { parseServerDate } from "@/lib/utils"

export type Dringlichkeit = "ueberfaellig" | "heute" | "woche" | "spaeter" | "ohne"

export const DRINGLICHKEITEN: { key: Dringlichkeit; label: string }[] = [
  { key: "ueberfaellig", label: "Überfällig" },
  { key: "heute", label: "Heute" },
  { key: "woche", label: "Diese Woche" },
  { key: "spaeter", label: "Später" },
  { key: "ohne", label: "Ohne Fälligkeit" },
]

/** Kalendertage von `heute` bis zum Fälligkeitsdatum (negativ = überfällig). */
export function tageBis(due: string | null | undefined, heute: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(due || "")
  if (!m) return null
  const ziel = Date.UTC(+m[1], +m[2] - 1, +m[3])
  const jetzt = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate())
  return Math.round((ziel - jetzt) / 86_400_000)
}

/** „Diese Woche“ = die nächsten 4 Tage — reicht von Montag bis Freitag. */
export function dringlichkeit(due: string | null | undefined, heute: Date = new Date()): Dringlichkeit {
  const t = tageBis(due, heute)
  if (t === null) return "ohne"
  if (t < 0) return "ueberfaellig"
  if (t === 0) return "heute"
  if (t <= 4) return "woche"
  return "spaeter"
}

export function faelligText(due: string | null | undefined, heute: Date = new Date()): string {
  const t = tageBis(due, heute)
  if (t === null) return ""
  if (t === -1) return "seit gestern"
  if (t < 0) return `seit ${-t} Tagen`
  if (t === 0) return "heute"
  if (t === 1) return "morgen"
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(due!)!
  const d = new Date(+m[1], +m[2] - 1, +m[3])
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })
}

export type HerkunftArt = "mail" | "anruf" | "whatsapp" | "hero" | "hand" | "ki"

/**
 * Woher die Aufgabe kommt, in Worten — mit Link an die Stelle im Portal, wenn es eine gibt
 * (Paul 03.11.2026). Die Rohwerte auf Prod (03.11.2026): hero, email-quick, agent, manual,
 * voice_agent:…, email:<id>, sipgate:<id>, voice:…, call:<id>, whatsapp, fortytools.
 * Seit 03.11.2026 auch email-quick:<mail-id> und agent:<mail-id>.
 * IDs stehen nur im Link, nie im Text. Das Datum steht separat („erstellt …“).
 */
export function herkunft(source: string | null | undefined): { art: HerkunftArt; text: string; link?: string } {
  const roh = source || ""
  const doppel = roh.indexOf(":")
  const art = doppel >= 0 ? roh.slice(0, doppel) : roh
  const ref = doppel >= 0 ? roh.slice(doppel + 1).trim() : ""
  const zahl = /^\d+$/.test(ref) ? ref : ""
  switch (art) {
    case "email": return { art: "mail", text: "aus Mail", link: zahl ? `/emails/${zahl}` : undefined }
    case "email-quick": return { art: "mail", text: "aus Mail", link: zahl ? `/emails/${zahl}` : undefined }
    case "sipgate": return { art: "anruf", text: "aus Anruf", link: zahl ? `/sipgate/${zahl}` : undefined }
    case "call": return { art: "anruf", text: "aus Anruf", link: zahl ? `/calls/${zahl}` : undefined }
    case "voice": case "voice_agent":
      return { art: "anruf", text: "aus KI Telefon", link: ref ? `/ki-telefon/${encodeURIComponent(ref)}` : "/ki-telefon" }
    case "whatsapp": return { art: "whatsapp", text: "aus WhatsApp", link: "/whatsapp" }
    case "agent": return { art: "ki", text: "vom Assistenten", link: zahl ? `/emails/${zahl}` : undefined }
    case "hero": case "fortytools": return { art: "hero", text: art === "hero" ? "aus HERO" : "aus fortytools" }
    default: return { art: "hand", text: "von Hand angelegt" }
  }
}

/** „erstellt 30.10. 14:32“ — HERO-Anlegedatum vor der lokalen Zeile (bei HERO-Aufgaben ist die nur die Sync-Zeit). */
export function erstelltText(heroCreated?: string | null, createdAt?: string | null): string {
  const d = parseServerDate(heroCreated || createdAt)
  if (!d) return ""
  const tag = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`
  const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  return `erstellt ${tag} ${zeit}`
}
